import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { motion } from "framer-motion";
import { Users, CurrencyDollar, Package, Globe, CheckCircle, XCircle, Truck, Tag } from "@phosphor-icons/react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip } from "recharts";
import { toast } from "sonner";

export default function AdminDashboard() {
  const { user, authHeaders, loading } = useAuth();
  const { format, code } = useCurrency();
  const [tab, setTab] = useState("overview");
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  const load = async () => {
    const [u, o, a] = await Promise.all([
      axios.get(`${API}/admin/users`, { headers: authHeaders(), withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API}/orders/admin`, { headers: authHeaders(), withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API}/analytics/admin`, { headers: authHeaders(), withCredentials: true }).catch(() => ({ data: null })),
    ]);
    setUsers(u.data);
    setOrders(o.data);
    setAnalytics(a.data);
  };

  useEffect(() => { if (user) load(); }, [user]);

  const approve = async (uid, approved) => {
    await axios.post(`${API}/admin/users/${uid}/approve`, { approved }, { headers: authHeaders(), withCredentials: true });
    toast.success(approved ? "Approved" : "Revoked");
    load();
  };

  const updateStatus = async (oid, status) => {
    await axios.post(`${API}/orders/${oid}/status`, { status }, { headers: authHeaders(), withCredentials: true });
    toast.success(`Order ${status}`);
    load();
  };

  if (loading) return <div className="min-h-screen pt-32 text-center text-muted">Loading...</div>;
  if (!user || user.role !== "admin") { window.location.href = "/login"; return null; }

  return (
    <div className="min-h-screen pt-24 bg-ink-950" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <p className="section-label">Admin console</p>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2">Vault Operations</h1>

        <div className="mt-8 flex gap-2 border-b border-white/5">
          {[
            { id: "overview", label: "Overview" },
            { id: "users", label: "Users" },
            { id: "orders", label: "Orders" },
          ].map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-5 py-3 text-sm border-b-2 ${tab === t.id ? "border-saffron text-cream" : "border-transparent text-muted hover:text-cream"}`}
              data-testid={`atab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && analytics && (
          <div className="mt-10 grid md:grid-cols-4 gap-5">
            <StatCard Icon={CurrencyDollar} label={`GMV · ${code}`} val={format(analytics.total_revenue)} />
            <StatCard Icon={Package} label="Orders" val={analytics.orders_count} />
            <StatCard Icon={Users} label="Farmers" val={analytics.farmer_count} />
            <StatCard Icon={Users} label="Buyers" val={analytics.buyer_count} />
            <div className="md:col-span-2 glass rounded-2xl p-6">
              <h3 className="font-display text-2xl mb-5 flex items-center gap-2"><Globe size={22} weight="duotone" className="text-saffron" /> Top origins by revenue</h3>
              <div className="h-[260px]">
                <ResponsiveContainer>
                  <BarChart data={analytics.top_origins}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="country" tick={{ fill: "#A3A39C", fontSize: 11 }} />
                    <YAxis tick={{ fill: "#A3A39C", fontSize: 11 }} />
                    <RTooltip contentStyle={{ background: "#0A1410", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} />
                    <Bar dataKey="revenue" fill="#F4A261" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="md:col-span-2 glass rounded-2xl p-6">
              <h3 className="font-display text-2xl mb-4">Live vault</h3>
              <div className="space-y-3">
                <div className="flex justify-between border-b border-white/5 pb-3"><span className="text-muted">Products listed</span><span className="text-cream font-display text-xl">{analytics.product_count}</span></div>
                <div className="flex justify-between border-b border-white/5 pb-3"><span className="text-muted">Active users</span><span className="text-cream font-display text-xl">{analytics.farmer_count + analytics.buyer_count}</span></div>
                <div className="flex justify-between"><span className="text-muted">Origin countries</span><span className="text-cream font-display text-xl">{analytics.top_origins?.length || 0}</span></div>
              </div>
            </div>
          </div>
        )}

        {tab === "users" && (
          <div className="mt-10 glass rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-ink-800/60"><tr className="text-left text-muted uppercase tracking-[0.2em] text-[10px]">
                <th className="px-5 py-4">User</th><th className="px-5 py-4">Role</th><th className="px-5 py-4">Company</th><th className="px-5 py-4">Status</th><th className="px-5 py-4">Actions</th>
              </tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className="border-t border-white/5" data-testid={`user-${u.user_id}`}>
                    <td className="px-5 py-4"><div className="text-cream">{u.name}</div><div className="text-[11px] text-muted">{u.email}</div></td>
                    <td className="px-5 py-4"><span className="chip">{u.role || "pending"}</span></td>
                    <td className="px-5 py-4 text-muted">{u.company || "—"}</td>
                    <td className="px-5 py-4">{u.approved ? <span className="text-forest">● approved</span> : <span className="text-red-400">● pending</span>}</td>
                    <td className="px-5 py-4">
                      {u.approved ? (
                        <button onClick={() => approve(u.user_id, false)} className="text-xs text-red-400 hover:text-red-300"><XCircle size={14} className="inline" /> Revoke</button>
                      ) : (
                        <button onClick={() => approve(u.user_id, true)} className="text-xs text-saffron"><CheckCircle size={14} className="inline" /> Approve</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "orders" && (
          <div className="mt-10 space-y-3">
            {orders.map((o) => (
              <div key={o.id} className="glass rounded-2xl p-5" data-testid={`order-${o.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-gold">Order #{o.id.slice(0, 8)}</div>
                    <div className="text-sm text-muted mt-1">{o.buyer_id} → {o.items.length} item(s)</div>
                    <div className="text-sm text-muted mt-1">
                      {o.items.map((it, i) => <div key={i}>• {it.branded_name} — {it.qty_kg}kg <span className="text-forest">({(it.farmer_id || "").slice(0, 10)})</span></div>)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-2xl text-saffron">{format(o.total_usd)}</div>
                    <span className="chip mt-2 inline-block">{o.status}</span>
                  </div>
                </div>
                <div className="mt-4 flex gap-2 flex-wrap">
                  {o.status === "paid" && <button onClick={() => updateStatus(o.id, "assigned")} className="btn-primary text-xs" data-testid={`assign-${o.id}`}>Assign to farmer</button>}
                  {o.status === "assigned" && <button onClick={() => updateStatus(o.id, "label_generated")} className="btn-secondary text-xs">Generate label</button>}
                  {o.status === "shipped" && <button onClick={() => updateStatus(o.id, "delivered")} className="btn-secondary text-xs">Mark delivered</button>}
                  <a href={`${API}/orders/${o.id}/label`} target="_blank" rel="noreferrer" className="btn-secondary text-xs"><Tag size={12} /> Print label</a>
                </div>
              </div>
            ))}
            {orders.length === 0 && <div className="glass rounded-2xl p-16 text-center text-muted">No orders yet.</div>}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ Icon, label, val }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl p-6">
      <Icon size={22} weight="duotone" className="text-saffron" />
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted mt-3">{label}</div>
      <div className="font-display text-3xl text-cream mt-1">{val}</div>
    </motion.div>
  );
}
